import {
  Commitment,
  Relationship,
} from "@/services/backend/relationships/types/relationship";
import { useRelationshipsFulfillmentListQuery } from "@/services/backend/relationships/api/fulfillment_service";
import { FulfillmentState } from "@/services/backend/relationships/types/fulfillment";
import { FilterMode } from "@/shared/lib/filter/filter";
import { Skeleton } from "@/shared/components/ui/skeleton";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { CreateFulfillmentDialog } from "@/services/backend/relationships/components/create-fulfillment-dialog";
import { DialogTrigger } from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import t from "@/lang/lang";
import { weekRFC3339 } from "@/shared/lib/utilities/date";
import { ServiceKind } from "@bitory-gmbh/bsm";
import { useDocumentPath } from "@/services/backend/documents/components/use-document-path";
import { GSMServiceKind } from "@/services/backend/services/types/kinds";
import { ReactNode } from "react";
import { DocumentReferenceNumberById } from "@/services/backend/documents/components/document-reference-number-by-id";

export function FulfillmentCell({
  relationship,
  commitment,
  renderDocumentLink,
}: {
  relationship: Relationship;
  commitment: Commitment;
  renderDocumentLink: (path: string, children: ReactNode) => ReactNode;
}) {
  const {
    data: list,
    isLoading,
    error,
  } = useRelationshipsFulfillmentListQuery({
    commitment: {
      active: true,
      values: [commitment.id],
    },
    // Ignore cancelled fulfillments: a cancelled (stornierte) work order must
    // not count as an existing fulfillment. Otherwise the "Planen" button stays
    // hidden after a storno, and the cell keeps showing the stale cancelled
    // work order, pushing users to create a duplicate commitment instead of
    // re-planning this one.
    state: {
      active: true,
      mode: FilterMode.Not,
      values: [FulfillmentState.Cancelled],
    },
  });

  if (isLoading) {
    return <Skeleton className="h-4 w-48" />;
  }

  if (error) {
    return <RTKQueryErrorAlert error={error} />;
  }

  return (
    <div>
      {list!.fulfillments.length === 0 && (
        <CreateFulfillmentDialog
          relationship={relationship}
          commitment={commitment}
          trigger={
            <DialogTrigger asChild>
              <Button size="sm" className="h-6">
                {t("Planen")}
              </Button>
            </DialogTrigger>
          }
        />
      )}
      {list!.fulfillments.map((fulfillment) => (
        <div className="space-x-2" key={fulfillment.id}>
          <DocumentLink
            documentId={fulfillment.documentId!}
            documentKind={fulfillment.documentKind!}
            serviceKind={fulfillment.serviceKind}
            processId={fulfillment.processId}
            renderDocumentLink={renderDocumentLink}
          />
          <span className="min-w-16">
            KW: {weekRFC3339(fulfillment.plannedDate)}
          </span>
        </div>
      ))}
    </div>
  );
}

function DocumentLink({
  documentId,
  documentKind,
  serviceKind,
  processId,
  renderDocumentLink,
}: {
  documentId: string;
  documentKind: string;
  serviceKind: ServiceKind;
  processId: string;
  renderDocumentLink: (path: string, children: ReactNode) => ReactNode;
}) {
  const { path } = useDocumentPath({
    documentId,
    documentKind,
    serviceKind: serviceKind as GSMServiceKind,
    processId,
  });

  return (
    <Button
      asChild
      variant="link"
      size="sm"
      className="inline h-6 px-0 whitespace-normal"
    >
      {renderDocumentLink(
        path!,
        <DocumentReferenceNumberById
          serviceKind={serviceKind}
          documentKind={documentKind}
          documentId={documentId}
        />,
      )}
    </Button>
  );
}
